import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'react-hot-toast';
import { 
  Truck, ArrowLeft, Save, Loader2, Package, Calendar, 
  MapPin, User, AlertCircle, CheckCircle2, Check
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CARRIERS = [
  { code: 'FEDEX', name: 'FedEx Express' },
  { code: 'UPS', name: 'UPS' },
  { code: 'DHL', name: 'DHL' },
  { code: 'USPS', name: 'USPS' },
  { code: 'BLUEDAR', name: 'BlueDart' },
  { code: 'INHOUSE', name: 'In-House Fleet' },
  { code: 'OTHER', name: 'Other' }
];

const ShipmentCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedDelivery = searchParams.get('delivery');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState({});

  const [formData, setFormData] = useState({
    carrier_code: 'INHOUSE',
    carrier_name: '',
    planned_ship_date: new Date().toISOString().split('T')[0],
    planned_delivery_date: '',
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    tracking_number: '',
    notes: ''
  });


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load deliveries that are ready for shipment (GOODS_ISSUED or packed)
      const response = await axios.get(`${BACKEND_URL}/api/otc/deliveries`);
      
      // Filter deliveries that have goods issued and not yet shipped
      const eligibleDeliveries = (response.data || []).filter(d => 
        d.status === 'GOODS_ISSUED' && 
        !d.shipment_number // Not already assigned to a shipment
      );
      
      setDeliveries(eligibleDeliveries);

      // Pre-select delivery if passed in URL
      if (preSelectedDelivery) {
        const delivery = eligibleDeliveries.find(d => d.delivery_number === preSelectedDelivery);
        if (delivery) {
          setSelectedDeliveries({ [preSelectedDelivery]: true });
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setIsLoading(false);
    }
  }, [preSelectedDelivery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeliveryToggle = (deliveryNumber) => {
    setSelectedDeliveries(prev => ({
      ...prev,
      [deliveryNumber]: !prev[deliveryNumber]
    }));
  };

  const handleSelectAll = () => {
    const allSelected = deliveries.length > 0 && deliveries.every(d => selectedDeliveries[d.delivery_number]);
    
    if (allSelected) {
      setSelectedDeliveries({});
    } else {
      const newSelected = {};
      deliveries.forEach(d => newSelected[d.delivery_number] = true);
      setSelectedDeliveries(newSelected);
    }
  };

  const getSelectedCount = () => {
    return Object.values(selectedDeliveries).filter(Boolean).length;
  };

  const handleCarrierChange = (code) => {
    const carrier = CARRIERS.find(c => c.code === code);
    setFormData({
      ...formData,
      carrier_code: code,
      carrier_name: carrier?.name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedDeliveryNumbers = Object.entries(selectedDeliveries)
      .filter(([_, selected]) => selected)
      .map(([num, _]) => num);

    if (selectedDeliveryNumbers.length === 0) {
      toast.error('Please select at least one delivery');
      return;
    }

    if (!formData.planned_ship_date) {
      toast.error('Please enter planned ship date');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        delivery_numbers: selectedDeliveryNumbers,
        carrier_code: formData.carrier_code,
        carrier_name: formData.carrier_name || CARRIERS.find(c => c.code === formData.carrier_code)?.name,
        planned_ship_date: formData.planned_ship_date,
        planned_delivery_date: formData.planned_delivery_date || null,
        vehicle_number: formData.vehicle_number || null,
        driver_name: formData.driver_name || null,
        driver_phone: formData.driver_phone || null,
        tracking_number: formData.tracking_number || null,
        notes: formData.notes || null,
        created_by: 'ADMIN'
      };

      const response = await axios.post(`${BACKEND_URL}/api/warehouse/shipments`, payload);
      
      toast.success(`Shipment ${response.data.shipment_number} created successfully!`);
      navigate(`/warehouse/shipments`);
    } catch (error) {
      console.error('Error creating shipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="shipment-create-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/warehouse/shipments')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Truck className="w-7 h-7 text-orange-600" />
              Create Shipment
            </h1>
            <p className="text-sm text-gray-500">Consolidate deliveries for transportation</p>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Settings */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Shipment Details</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Carrier *</Label>
                      <select
                        value={formData.carrier_code}
                        onChange={(e) => handleCarrierChange(e.target.value)}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                        required
                      >
                        {CARRIERS.map(c => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <Label>Planned Ship Date *</Label>
                      <Input
                        type="date"
                        value={formData.planned_ship_date}
                        onChange={(e) => setFormData({ ...formData, planned_ship_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>

                    <div>
                      <Label>Expected Delivery Date</Label>
                      <Input
                        type="date"
                        value={formData.planned_delivery_date}
                        onChange={(e) => setFormData({ ...formData, planned_delivery_date: e.target.value })}
                        min={formData.planned_ship_date}
                      />
                    </div>

                    <div>
                      <Label>Tracking Number</Label>
                      <Input
                        value={formData.tracking_number}
                        onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                        placeholder="Enter tracking number"
                      />
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Vehicle & Driver</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Vehicle Number</Label>
                      <Input
                        value={formData.vehicle_number}
                        onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })}
                        placeholder="e.g., TN-01-AB-1234"
                      />
                    </div>

                    <div>
                      <Label>Driver Name</Label>
                      <Input
                        value={formData.driver_name}
                        onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })}
                        placeholder="Enter driver name"
                      />
                    </div>

                    <div>
                      <Label>Driver Phone</Label>
                      <Input
                        value={formData.driver_phone}
                        onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })}
                        placeholder="+91 9876543210"
                      />
                    </div>
                  </div>
                </Card>

                {/* Summary */}
                <Card className="p-6 bg-orange-50 border-orange-200">
                  <h3 className="text-lg font-semibold mb-4 text-orange-800">Selection Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deliveries Selected:</span>
                      <span className="font-semibold text-orange-800">{getSelectedCount()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Carrier:</span>
                      <span className="font-semibold text-orange-800">
                        {CARRIERS.find(c => c.code === formData.carrier_code)?.name}
                      </span>
                    </div>
                  </div>
                </Card>

                <Button 
                  type="submit" 
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={isSaving || getSelectedCount() === 0}
                >
                  {isSaving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Create Shipment</>
                  )}
                </Button>
              </div>

              {/* Right Column - Deliveries */}
              <div className="lg:col-span-2">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Deliveries Ready for Shipment</h3>
                    {deliveries.length > 0 && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        {deliveries.every(d => selectedDeliveries[d.delivery_number]) ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </div>

                  {deliveries.length === 0 ? (
                    <div className="text-center py-8">
                      <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-gray-500">No deliveries ready for shipment</p>
                      <p className="text-sm text-gray-400 mt-1">
                        Deliveries must have Goods Issue posted first
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {deliveries.map(delivery => (
                        <div
                          key={delivery.delivery_number}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedDeliveries[delivery.delivery_number]
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => handleDeliveryToggle(delivery.delivery_number)}
                        >
                          <div className="flex items-start gap-3">
                            <div 
                              className={`h-4 w-4 shrink-0 rounded-sm border shadow flex items-center justify-center cursor-pointer ${
                                selectedDeliveries[delivery.delivery_number] 
                                  ? 'bg-orange-600 border-orange-600 text-white' 
                                  : 'border-gray-300 bg-white'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeliveryToggle(delivery.delivery_number);
                              }}
                            >
                              {selectedDeliveries[delivery.delivery_number] && (
                                <Check className="h-3 w-3" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className="font-semibold text-gray-900">
                                  {delivery.delivery_number}
                                </span>
                                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">
                                  {delivery.status}
                                </span>
                              </div>
                              <div className="mt-1 text-sm text-gray-600">
                                <div className="flex items-center gap-4">
                                  <span>SO: {delivery.sales_order_number}</span>
                                  <span>Items: {delivery.items?.length || 0}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  <span>{delivery.ship_to_party_name || delivery.ship_to_party}</span>
                                </div>
                                {delivery.actual_goods_issue_date && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <Calendar className="w-3 h-3" />
                                    <span>GI: {delivery.actual_goods_issue_date}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
};

export default ShipmentCreate;
